import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BookingCalculationService } from '@roller/booking-calculation-service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { DateFormatService } from '../date/date-format.service';
import { INumberIncrementProductItem } from '../form/number-increment/number-increment-product-item.model';
import { IProductInfo } from '../product/models/product-info.model';
import { IProductSession } from '../product/models/product-session.model';
import { IProduct } from '../product/models/product.model';
import { VenueCurrencyPipe } from '../venue-currency/venue-currency.pipe';
import { IUpgradeFromTo, IUpgradeHardCoded, IVenueUpgradeItem } from './upgrade-hard-coded.model';
import { IOrderItem } from '@core/order/models/order-item.model';
import { VenueConfigService } from '@core/venue-config/venue-config.service';
import { ProductAvailableService } from '@core/product/product-available.service';

@Injectable({
  providedIn: 'root'
})
export class UpgradeService {
  private upgradeList$: BehaviorSubject<IUpgradeHardCoded[]> = new BehaviorSubject<IUpgradeHardCoded[]>([]);
  private _venueUpgradeList: IVenueUpgradeItem[] | undefined;
  private _hasOfferBeenPresented = false;
  private venueCurrencyPipe = new VenueCurrencyPipe();

  constructor(
    private http: HttpClient,
    private dateFormatService: DateFormatService,
    private bookingCalculationService: BookingCalculationService,
    private venueConfigService: VenueConfigService,
    private productAvailableService: ProductAvailableService
  ) {}

  /** Triggers when the upgrade modal opens to set to true so does not present again until the guest refreshes */
  setOfferHasBeenPresented(): void {
    this._hasOfferBeenPresented = true;
  }

  /** Fetches the hard coded list of upgrades from the assets folder, finds any offers related to the current venue/checkout */
  async fetchUpgradeList(venueSlug: string, checkoutName: string): Promise<IVenueUpgradeItem[] | undefined> {
    const list = await firstValueFrom(this.http.get<IUpgradeHardCoded[]>('./assets/config/upgrade-hard-coded.json'));
    this.upgradeList$.next(list);
    this._venueUpgradeList = list.find(
      (upgrade: IUpgradeHardCoded) => upgrade.venueSlug === venueSlug && upgrade.checkoutName === checkoutName
    )?.upgrades;
    return this._venueUpgradeList;
  }

  /** Find product and ticket from a ticketId (they should all be unique) */
  private findTicketId(productList: IProduct[], ticketId: string): [IProduct | undefined, IProductInfo | undefined] {
    let ticketInfo: IProductInfo | undefined;
    const product = productList.find((product: IProduct) => {
      ticketInfo = product.products.find((info: IProductInfo) => info.id === ticketId);
      return !!ticketInfo;
    });

    return [product, ticketInfo];
  }

  /** Finds the first relevant upgrade offer where the checkout basket ticket id matches */
  upgradeOfferRelevant(
    basket: IOrderItem[],
    selectedDate: Date,
    selectedSession: IProductSession | undefined,
    productListWithAvailability: IProduct[],
    fromProduct: IProduct,
    relatedOrderItemKey: string
  ): IVenueUpgradeItem | undefined {
    basket = basket.filter((item: IOrderItem) => item.relatedOrderItemKey === relatedOrderItemKey);
    if (!this._venueUpgradeList || !this._venueUpgradeList.length || !basket.length || this._hasOfferBeenPresented) {
      return undefined;
    }

    const currency = this.venueConfigService.getCurrencySettings();
    let totalOriginalCost = 0;
    const upgradeList = structuredClone(this._venueUpgradeList).find((upgrade: IVenueUpgradeItem) => {
      let isTicketMatch = false;
      const allBasketCanBeUpgraded = basket.every((item: IOrderItem) => {
        if (!this.dateFormatService.isSameDay(item.bookingDate, selectedDate)) {
          return false;
        }

        const findTicketMatch = upgrade.ticketMatch.find(
          (upgradeToFrom: IUpgradeFromTo) => upgradeToFrom.from === item.product.id
        );
        if (!findTicketMatch) {
          return false;
        }

        isTicketMatch = true;
        upgrade.fromProduct = fromProduct;
        upgrade.fromSession = selectedSession;
        findTicketMatch.fromTicket = item.product;
        findTicketMatch.upgradeUniqueBasketId = item.uniqueId;
        [upgrade.toProduct, findTicketMatch.toTicket] = this.findTicketId(
          productListWithAvailability,
          findTicketMatch.to
        );
        if (!findTicketMatch.toTicket) {
          return false;
        }

        const toTicket: IProductInfo = findTicketMatch.toTicket;
        const costBookingItem = item;
        const fromTicketCost = this.bookingCalculationService.getTicketCost(costBookingItem) * costBookingItem.quantity;
        totalOriginalCost += fromTicketCost;
        const toTicketCost =
          this.bookingCalculationService.getTicketCost({
            ...costBookingItem,
            cost: toTicket.cost,
            tax: toTicket.tax,
            groupSize: toTicket.groupSize,
            isTaxInclusive: toTicket.isTaxInclusive,
            perProductBookingFee: toTicket.perProductBookingFee
          }) * costBookingItem.quantity;

        findTicketMatch.upgradeCost = toTicketCost - fromTicketCost;
        return findTicketMatch.upgradeCost > 0;
      });
      upgrade.originalCost = this.venueCurrencyPipe.transform(totalOriginalCost, currency);

      return allBasketCanBeUpgraded && isTicketMatch;
    });
    if (!upgradeList) {
      return undefined;
    }

    if (upgradeList.fromProduct?.type === 'membership') {
      const onlyUpgradeOne = upgradeList.ticketMatch.find(
        (match: IUpgradeFromTo) => match.toTicket && match.fromTicket
      );
      if (onlyUpgradeOne) {
        upgradeList.ticketMatch = [onlyUpgradeOne];
      } else {
        return undefined;
      }
    }

    // verify that there is availability and session for the upgrade
    if (upgradeList.toProduct?.isItemSessionBased && upgradeList.toProduct.sessions) {
      const matchingSession = upgradeList.toProduct.sessions.find(
        (session: IProductSession) =>
          this.dateFormatService.isSameDay(session.date, selectedSession?.date) &&
          session.name === selectedSession?.name &&
          session.startTime === selectedSession?.startTime
      );
      if (!matchingSession || !matchingSession.onlineSalesOpen) {
        return undefined;
      }

      const ensureCapacityToUpgrade = upgradeList.ticketMatch.every((match: IUpgradeFromTo) => {
        // if no related unique upgrade item, then we don't need to consider it
        if (!match.upgradeUniqueBasketId || !match.toTicket) {
          return true;
        }

        return this.productAvailableService.canAddProduct(match.toTicket, basket, selectedDate, matchingSession);
      });
      if (!ensureCapacityToUpgrade) {
        return undefined;
      }

      upgradeList.toSession = matchingSession;
    }

    const upgradeCostDifference = this.venueCurrencyPipe.transform(
      upgradeList.ticketMatch.reduce((acc: number, match: IUpgradeFromTo) => acc + (match.upgradeCost || 0), 0),
      currency
    );

    return {
      ...upgradeList,
      upgradeCostDifference
    };
  }

  getUpgradeOrderItem(items: IOrderItem[], upgrade: IUpgradeFromTo) {
    return items.find(
      (item: IOrderItem) => upgrade.upgradeUniqueBasketId && item.uniqueId === upgrade.upgradeUniqueBasketId
    );
  }

  getUpgradeIncrement(item: IOrderItem, upgrade: IUpgradeFromTo): INumberIncrementProductItem {
    return {
      id: upgrade.to,
      quantity: item.quantity,
      product: upgrade.toTicket as IProductInfo
    };
  }
}
